import type ContactPhone from "@/model/interface/ContactPhone";
import { ContactPhoneStatus } from "@/enum/ContactPhoneStatus";
import { DncListStatus } from "@/enum/DncListStatus";
import { formatClockTime } from "@/util/date";

export const MAX_LINES = 3;

export const MAX_PHONES_PER_LEAD = 10;

export const MAX_AI_CALLS = 10;

export const MAX_RETRY_ATTEMPTS = 5;

export const MAX_DAILY_ATTEMPTS = 5;

export const DEFAULT_RETRY_MINUTES = 60;

export const RETRY_DELAY_MINUTES = [5, 10, 15, 30, 60, 120, 240, 1440];

export function formatRetryDelay(minutes: number): string {
  if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    return days === 1 ? "1 day" : `${days} days`;
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? "1 hour" : `${hours} hours`;
  }
  return minutes === 1 ? "1 minute" : `${minutes} minutes`;
}

export function retryDelayChoices(current: number | null): number[] {
  if (current === null || RETRY_DELAY_MINUTES.includes(current)) {
    return RETRY_DELAY_MINUTES;
  }
  return [...RETRY_DELAY_MINUTES, current].sort((first, second) => first - second);
}

export const MIN_RING_SECONDS = 15;

export const DIALER_ROOM_PREFIX = "pds-";

export const LINE_REFRESH_AFTER_MS = 13_200_000;

export function phoneSkipLabel(phone: ContactPhone): string | null {
  if (phone.status === ContactPhoneStatus.WRONG_NUMBER) {
    return "Wrong number";
  }
  if (phone.status === ContactPhoneStatus.DISCONNECTED) {
    return "Disconnected";
  }
  if (phone.doNotCall) {
    return "Do not call";
  }
  if (phone.litigator || phone.dncListStatus === DncListStatus.LITIGATOR) {
    return "Known litigator";
  }
  if (phone.dncListStatus === DncListStatus.NATIONAL_LISTED || phone.dncListStatus === DncListStatus.STATE_LISTED) {
    return "On a Do Not Call list";
  }
  return null;
}

export function emptyQueueMessage(nextDueAt?: string | null): string {
  if (!nextDueAt) {
    return "No leads left in the queue.";
  }
  return `Nothing is due right now. The next retry or callback is due at ${formatClockTime(nextDueAt)}.`;
}

export function formatAbandonRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

export function connectSeconds(answeredAt?: string, connectedAt?: string): string | null {
  if (!answeredAt || !connectedAt) {
    return null;
  }
  const milliseconds = new Date(connectedAt).getTime() - new Date(answeredAt).getTime();
  if (milliseconds < 0) {
    return null;
  }
  return (milliseconds / 1000).toFixed(1);
}
